import type { TeammatePlaybookCriteriaScore } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  computeTeammatePlaybookCoachingDates,
  type CoachingContentV1,
  type CoachingContentV2,
  type CoachingContentV3,
  type TeammatePlaybookCoaching,
} from "@repo/app-types/entities/teammatePlaybookCoaching";
import { renderPlural } from "@repo/app-types/helpers/utilities";
import type { DateTime } from "luxon";

export function findCurrentCriterionCoachingNote(
  coaching: TeammatePlaybookCoaching | undefined,
  criterionId: string,
  now: DateTime,
): CoachingContentV1 | CoachingContentV2 | CoachingContentV3 | undefined {
  if (!coaching) {
    return undefined;
  }

  const { goalEvaluationWindowStart, goalEvaluationWindowEnd } =
    computeTeammatePlaybookCoachingDates(coaching);
  if (now < goalEvaluationWindowStart || now >= goalEvaluationWindowEnd) {
    return undefined;
  }

  return coaching.content.criteria.find(
    ({ criterion_id }) => criterion_id === criterionId,
  )?.content;
}

export function buildCriterionCallSummary(
  score: TeammatePlaybookCriteriaScore | undefined,
  reviewedCallCount: number | undefined,
): string | undefined {
  if (reviewedCallCount === undefined) {
    return undefined;
  }

  const applicableCallCount = Object.entries(
    score?.score_distribution.counts ?? {},
  ).reduce(
    (count, [value, frequency]) =>
      value === "N/A" ? count : count + (frequency ?? 0),
    0,
  );
  const applicableCalls = renderPlural(applicableCallCount, {
    one: "1 applicable call",
    other: (count) => `${count.toLocaleString()} applicable calls`,
  });
  const reviewedCalls = renderPlural(reviewedCallCount, {
    one: "1 call reviewed",
    other: (count) => `${count.toLocaleString()} calls reviewed`,
  });

  if (
    !score ||
    !Number.isFinite(score.average_score) ||
    applicableCallCount === 0
  ) {
    return `${applicableCalls} · ${reviewedCalls}`;
  }

  const average =
    score.score_distribution.valueType === "1_5_score"
      ? `${score.average_score.toFixed(1)} out of 5`
      : `${(score.average_score * 100).toFixed(0)}% pass rate`;
  return `${average} across ${applicableCalls} · ${reviewedCalls}`;
}
